import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
  Chip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

/**
 * FeatureGuard - A component to protect features based on subscription level
 *
 * @param {Object} props
 * @param {string} props.tier - The subscription tier required ('basic', 'standard', 'starter_studio', 'growth_studio')
 * @param {string} props.feature - Specific feature requirement ('email_reminders', 'studio_management', 'advanced_analytics', etc.)
 * @param {React.ReactNode} props.children - The content to protect
 * @param {boolean} props.showUpgradeButton - Whether to show an upgrade button
 * @param {boolean} props.showPreview - Whether to show a blurred preview of the content
 * @param {string} props.message - Custom message to display (optional)
 * @param {Object} props.sx - Additional styles for the container
 */
const FeatureGuard = ({
  tier = 'standard',
  feature = null,
  children,
  showUpgradeButton = true,
  showPreview = false,
  message,
  sx = {}
}) => {
  const { getSubscriptionLevel } = UseAuthRoles();
  const navigate = useNavigate();
  const currentTier = getSubscriptionLevel();

  // Define tier hierarchy
  const tiers = ['unpaid', 'basic', 'standard', 'starter_studio', 'growth_studio'];
  const currentTierIndex = tiers.indexOf(currentTier);
  const requiredTierIndex = tiers.indexOf(tier);

  // Feature-based access control
  const featureAccess = {
    'email_reminders': ['standard', 'growth_studio'],
    'aftercare_emails': ['standard', 'growth_studio'],
    'studio_management': ['starter_studio', 'growth_studio'],
    'multi_user': ['starter_studio', 'growth_studio'],
    'advanced_analytics': ['growth_studio'],
    'priority_support': ['growth_studio'],
    'custom_integrations': ['growth_studio'],
    'team_scheduling': ['starter_studio', 'growth_studio'],
    'studio_analytics': ['starter_studio', 'growth_studio'],
    'white_label': ['growth_studio']
  };

  // Check access based on feature or tier
  let hasAccess = false;
  let requiredPlans = [];

  if (feature && featureAccess[feature]) {
    // Feature-based access
    hasAccess = featureAccess[feature].includes(currentTier);
    requiredPlans = featureAccess[feature];
  } else {
    // Tier-based access (backwards compatibility)
    hasAccess = currentTierIndex >= requiredTierIndex;
    requiredPlans = tiers.slice(requiredTierIndex);
  }

  // Return children if user has access
  if (hasAccess) {
    return children;
  }

  // Get display information
  const getFeatureInfo = () => {
    if (feature) {
      const featureNames = {
        'email_reminders': 'Email Reminders',
        'aftercare_emails': 'Aftercare Emails',
        'studio_management': 'Studio Management',
        'multi_user': 'Multi-User Access',
        'advanced_analytics': 'Advanced Analytics',
        'priority_support': 'Priority Support',
        'custom_integrations': 'Custom Integrations',
        'team_scheduling': 'Team Scheduling',
        'studio_analytics': 'Studio Analytics',
        'white_label': 'White Label Options'
      };
      
      return {
        name: featureNames[feature] || feature,
        icon: getFeatureIcon(feature),
        minPlan: getMinimumPlan(requiredPlans)
      };
    }
    
    return {
      name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Feature`,
      icon: <LockIcon />,
      minPlan: tier
    };
  };

  const getFeatureIcon = (featureName) => {
    switch (featureName) {
      case 'studio_management':
      case 'multi_user':
      case 'team_scheduling':
        return <PeopleIcon />;
      case 'advanced_analytics':
      case 'studio_analytics':
        return <BusinessIcon />;
      default:
        return <LockIcon />;
    }
  };

  const getMinimumPlan = (plans) => {
    // Return the lowest tier that has access
    const tierOrder = ['basic', 'standard', 'starter_studio', 'growth_studio'];
    for (const tier of tierOrder) {
      if (plans.includes(tier)) {
        return tier;
      }
    }
    return plans[0];
  };

  const getPlanDisplayName = (planName) => {
    const displayNames = {
      'basic': 'Basic',
      'standard': 'Standard',
      'starter_studio': 'Starter Studio',
      'growth_studio': 'Growth Studio'
    };
    return displayNames[planName] || planName;
  };

  const isStudioFeature = (featureName) => {
    const studioFeatures = ['studio_management', 'multi_user', 'team_scheduling', 'studio_analytics'];
    return studioFeatures.includes(featureName);
  };

  const featureInfo = getFeatureInfo();
  
  // Custom message or default
  const displayMessage = message || (
    feature 
      ? `This feature is available with ${requiredPlans.map(p => getPlanDisplayName(p)).join(', ')} subscription${requiredPlans.length > 1 ? 's' : ''}.`
      : `This feature requires a ${getPlanDisplayName(tier)} subscription or higher.`
  );

  // Show preview with overlay
  if (showPreview) {
    return (
      <Box sx={{ position: 'relative', ...sx }}>
        {/* Blurred content */}
        <Box sx={{
          filter: 'blur(3px)',
          pointerEvents: 'none',
          opacity: 0.7
        }}>
          {children}
        </Box>
        
        {/* Overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.6)',
          borderRadius: '4px',
          padding: 3,
          zIndex: 10
        }}>
          <Box sx={{ color: 'white', mb: 2, fontSize: 40 }}>
            {featureInfo.icon}
          </Box>
          
          <Typography variant="h6" color="white" gutterBottom>
            {featureInfo.name}
          </Typography>
          
          <Typography variant="body2" color="white" sx={{ mb: 2, textAlign: 'center' }}>
            {displayMessage}
          </Typography>

          {/* Show required plans */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {requiredPlans.map(plan => (
              <Chip
                key={plan}
                label={getPlanDisplayName(plan)}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}
              />
            ))}
          </Box>
          
          {showUpgradeButton && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/subscription')}
            >
              {isStudioFeature(feature) ? 'Upgrade to Studio' : 'Upgrade Now'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Show placeholder
  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        textAlign: 'center',
        border: '1px dashed #ccc',
        backgroundColor: 'rgba(0,0,0,0.02)',
        ...sx
      }}
    >
      <Tooltip title={`Your current plan: ${getPlanDisplayName(currentTier)}`}>
        <Box sx={{ color: 'text.secondary', mb: 1, fontSize: 30 }}>
          {featureInfo.icon}
        </Box>
      </Tooltip>
      
      <Typography variant="h6" gutterBottom>
        {featureInfo.name}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2 }}>
        {displayMessage}
      </Typography>

      {/* Show current vs required plan */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          label={`Current: ${getPlanDisplayName(currentTier)}`}
          size="small"
          color="default"
          variant="outlined"
        />
        <Typography variant="body2" color="text.secondary">→</Typography>
        <Chip
          label={`Required: ${getPlanDisplayName(featureInfo.minPlan)}`}
          size="small"
          color="primary"
        />
      </Box>
      
      {showUpgradeButton && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/subscription')}
        >
          {isStudioFeature(feature) ? 'Upgrade to Studio Plan' : 'Upgrade Subscription'}
        </Button>
      )}
    </Paper>
  );
};

export default FeatureGuard;